import type { FC } from 'react';
import { AbnormalBadge2 } from '@/components/semantic-badge/abnormal-badge-2';
import { AttentionBadge } from '@/components/semantic-badge/attention-badge';
import { DoctorOrderBadge } from '@/components/semantic-badge/doctor-order-badge';
import { EmergencyHandlingBadge } from '@/components/semantic-badge/emergency-handling-badge';
import { HighRiskBadge } from '@/components/semantic-badge/high-risk-badge';
import { LaborProgressBadge } from '@/components/semantic-badge/labor-progress-badge';
import { NormalBadge2 } from '@/components/semantic-badge/normal-badge-2';
import { NursingNoteBadge } from '@/components/semantic-badge/nursing-note-badge';
import { ObservationBadge } from '@/components/semantic-badge/observation-badge';
import { WarningBadge2 } from '@/components/semantic-badge/warning-badge-2';
import { AbnormalBadge } from './abnormal-badge';
import { AdmittedTodayBadge } from './admitted-today-badge';
import { AlertFlagBadge } from './alert-flag-badge';
import { AllergyFlagBadge } from './allergy-flag-badge';
import { BedOccupiedBadge } from './bed-occupied-badge';
import { BedVacantBadge } from './bed-vacant-badge';
import { CancelledOrderBadge } from './cancelled-order-badge';
import { CancerScreeningIneligibleBadge } from './cancer-screening-ineligible-badge';
import { CancerScreeningNotScreenedBadge } from './cancer-screening-not-screened-badge';
import { CancerScreeningScreenedBadge } from './cancer-screening-screened-badge';
import { CatastrophicIllnessFlagBadge } from './catastrophic-illness-flag-badge';
import { ControlBadge } from './control-badge';
import { ControlledDrug1Badge } from './controlled-drug-1-badge';
import { ControlledDrug2Badge } from './controlled-drug-2-badge';
import { ControlledDrug3Badge } from './controlled-drug-3-badge';
import { ControlledDrug4Badge } from './controlled-drug-4-badge';
import { CriticalBadge } from './critical-badge';
import { CriticallyHighRiskBadge } from './critically-high-risk-badge';
import { DisabledFormBadge } from './disabled-form-badge';
import { DischargedBadge } from './discharged-badge';
import { DnrFlagBadge } from './dnr-flag-badge';
import { EarlyMedicationBadge } from './early-medication-badge';
import { EmergencyBadge } from './emergency-badge';
import { FastingBadge } from './fasting-badge';
import { FormCancelledBadge } from './form-cancelled-badge';
import { FormCompletedBadge } from './form-completed-badge';
import { FormDraftBadge } from './form-draft-badge';
import { FormIncompleteBadge } from './form-incomplete-badge';
import { FormPendingReviewBadge } from './form-pending-review-badge';
import { FormPendingSignatureBadge } from './form-pending-signature-badge';
import { FormSignedBadge } from './form-signed-badge';
import { HighBadge } from './high-badge';
import { InjectionBadge } from './injection-badge';
import { InpatientBadge } from './inpatient-badge';
import { IsolationBadge } from './isolation-badge';
import { LaborActiveBadge } from './labor-active-badge';
import { LaborEarlyBadge } from './labor-early-badge';
import { LaborInBadge } from './labor-in-badge';
import { LaborOutBadge } from './labor-out-badge';
import { LaborTransitionBadge } from './labor-transition-badge';
import { LaborWaitingBadge } from './labor-waiting-badge';
import { LowBadge } from './low-badge';
import { MedicationCompletedBadge } from './medication-completed-badge';
import { MedicationExecutedBadge } from './medication-executed-badge';
import { MedicationInProgressBadge } from './medication-in-progress-badge';
import { MedicationNotExecutedBadge } from './medication-not-executed-badge';
import { MedicationNotPerformedBadge } from './medication-not-performed-badge';
import { MedicationOverdueBadge } from './medication-overdue-badge';
import { MedicationPendingBadge } from './medication-pending-badge';
import { NewBadge } from './new-badge';
import { NormalBadge } from './normal-badge';
import { OrderChangeNewBadge } from './order-change-new-badge';
import { OrderChangeStoppedBadge } from './order-change-stopped-badge';
import { OutpatientBadge } from './outpatient-badge';
import { PatientStatusInpatientBadge } from './patient-status-inpatient-badge';
import { PhysicalCheckBadge } from './physical-check-badge';
import { PregnancyCategoryABadge } from './pregnancy-category-a-badge';
import { PregnancyCategoryBBadge } from './pregnancy-category-b-badge';
import { PregnancyCategoryCBadge } from './pregnancy-category-c-badge';
import { PregnancyCategoryDBadge } from './pregnancy-category-d-badge';
import { PregnancyCategoryNBadge } from './pregnancy-category-n-badge';
import { PregnancyCategoryXBadge } from './pregnancy-category-x-badge';
import { PrnBadge } from './prn-badge';
import { StatBadge } from './stat-badge';
import { StoppedOrderBadge } from './stopped-order-badge';
import { TopicalBadge } from './topical-badge';
import { UndetectedBadge } from './undetected-badge';
import { UnknownBadge } from './unknown-badge';
import { UnsavedBadge } from './unsaved-badge';
import { UrgentBadge } from './urgent-badge';
import { WarningBadge } from './warning-badge';

const BADGE_COMPONENT_MAP = {
  outpatient: OutpatientBadge,
  inpatient: InpatientBadge,
  emergency: EmergencyBadge,
  physicalCheck: PhysicalCheckBadge,

  normal: NormalBadge,
  normal2: NormalBadge2,
  high: HighBadge,
  low: LowBadge,
  critical: CriticalBadge,
  abnormal: AbnormalBadge,
  abnormal2: AbnormalBadge2,
  undetected: UndetectedBadge,
  warning: WarningBadge,
  warning2: WarningBadge2,
  control: ControlBadge,

  patientStatusInpatient: PatientStatusInpatientBadge,
  discharged: DischargedBadge,
  admittedToday: AdmittedTodayBadge,

  bedOccupied: BedOccupiedBadge,
  bedVacant: BedVacantBadge,

  isolation: IsolationBadge,
  fasting: FastingBadge,
  alertFlag: AlertFlagBadge,
  allergyFlag: AllergyFlagBadge,
  catastrophicIllnessFlag: CatastrophicIllnessFlagBadge,
  dnrFlag: DnrFlagBadge,

  controlledDrug1: ControlledDrug1Badge,
  controlledDrug2: ControlledDrug2Badge,
  controlledDrug3: ControlledDrug3Badge,
  controlledDrug4: ControlledDrug4Badge,

  pregnancyCategoryA: PregnancyCategoryABadge,
  pregnancyCategoryB: PregnancyCategoryBBadge,
  pregnancyCategoryC: PregnancyCategoryCBadge,
  pregnancyCategoryD: PregnancyCategoryDBadge,
  pregnancyCategoryX: PregnancyCategoryXBadge,
  pregnancyCategoryN: PregnancyCategoryNBadge,

  injection: InjectionBadge,
  stoppedOrder: StoppedOrderBadge,
  cancelledOrder: CancelledOrderBadge,
  stat: StatBadge,
  prn: PrnBadge,
  orderChangeNew: OrderChangeNewBadge,
  orderChangeStopped: OrderChangeStoppedBadge,
  criticallyHighRisk: CriticallyHighRiskBadge,
  earlyMedication: EarlyMedicationBadge,
  topical: TopicalBadge,

  unsaved: UnsavedBadge,
  disabledForm: DisabledFormBadge,
  formCompleted: FormCompletedBadge,
  formIncomplete: FormIncompleteBadge,
  formCancelled: FormCancelledBadge,
  formDraft: FormDraftBadge,
  formSigned: FormSignedBadge,
  formPendingReview: FormPendingReviewBadge,
  formPendingSignature: FormPendingSignatureBadge,

  cancerScreeningScreened: CancerScreeningScreenedBadge,
  cancerScreeningNotScreened: CancerScreeningNotScreenedBadge,
  cancerScreeningIneligible: CancerScreeningIneligibleBadge,

  transitionPhase: LaborTransitionBadge,
  earlyPhase: LaborEarlyBadge,
  transferringOut: LaborOutBadge,
  transferringIn: LaborInBadge,
  activePhase: LaborActiveBadge,
  waitingPhase: LaborWaitingBadge,

  urgent: UrgentBadge,
  highRisk: HighRiskBadge,
  attention: AttentionBadge,
  newFlag: NewBadge,
  emergencyHandling: EmergencyHandlingBadge,
  observation: ObservationBadge,

  doctorOrder: DoctorOrderBadge,
  nursingNote: NursingNoteBadge,
  laborProgress: LaborProgressBadge,

  medicationExecuted: MedicationExecutedBadge,
  medicationCompleted: MedicationCompletedBadge,
  medicationNotExecuted: MedicationNotExecutedBadge,
  medicationNotPerformed: MedicationNotPerformedBadge,
  medicationInProgress: MedicationInProgressBadge,
  medicationPending: MedicationPendingBadge,
  medicationOverdue: MedicationOverdueBadge,

  unknown: UnknownBadge,
} as const satisfies Record<string, FC>;

type RegistryName = keyof typeof BADGE_COMPONENT_MAP;

const ALIAS_MAP = {
  'admitted-today': 'admittedToday',
  N: 'normal',
  H: 'high',
  HIGH: 'high',
  L: 'low',
  A: 'abnormal',
  AA: 'critical',
  OP: 'outpatient',
  IP: 'inpatient',
  ER: 'emergency',
  PE: 'physicalCheck',
  IN: 'patientStatusInpatient',
  TODAY: 'admittedToday',
  DISC: 'discharged',
  cancerScreening_screened: 'cancerScreeningScreened',
  cancerScreening_notScreened: 'cancerScreeningNotScreened',
  cancerScreening_ineligible: 'cancerScreeningIneligible',
  new: 'orderChangeNew',
  stopped: 'orderChangeStopped',
  draft: 'formDraft',
  completed: 'formCompleted',
  signed: 'formSigned',
  pending: 'formIncomplete',
  pendingReview: 'formPendingReview',
  pendingSignature: 'formPendingSignature',
  pendingReviewAndSignature: 'formPendingReview',
  cancelled: 'formCancelled',
  WAITING_PHASE: 'waitingPhase',
  EARLY_PHASE: 'earlyPhase',
  ACTIVE_PHASE: 'activePhase',
  TRANSITION_PHASE: 'transitionPhase',
  TRANSFERRING_OUT: 'transferringOut',
  TRANSFERRING_IN: 'transferringIn',
  PENDING_IN: 'transferringIn',
  DOCTOR_ORDER: 'doctorOrder',
  NURSE_ORDER: 'nursingNote',
  DELIVERY_PROCESS: 'laborProgress',
} as const satisfies Record<string, RegistryName>;

type AliasName = keyof typeof ALIAS_MAP;
export type SemanticBadgeName = RegistryName | AliasName;

const isAliasName = (key: string): key is AliasName => key in ALIAS_MAP;

type SemanticBadgeProps = {
  name: SemanticBadgeName;
};

export const SemanticBadge: FC<SemanticBadgeProps> = ({ name }) => {
  const canonicalName: RegistryName = isAliasName(name)
    ? ALIAS_MAP[name]
    : name;
  const BadgeComponent = BADGE_COMPONENT_MAP[canonicalName];
  if (!BadgeComponent) {
    if (import.meta.env.DEV) {
      console.warn(`[SemanticBadge] Unknown name: "${name}"`);
    }
    return <UnknownBadge />;
  }
  return <BadgeComponent />;
};
